import { useEffect, useState } from 'react'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { Button } from '@/components/ui/button'
import { EmployerPostingPreferencesFields } from '@/components/employer/employer-posting-preferences-fields'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import { defaultEntityCountryCode } from '@/utils/entity-location-fields'
import {
  applicationReceivedEmailEnabled,
  applicationReceivedInAppEnabled,
  saveApplicationReceivedEmailPreference,
} from '@/lib/employer-application-notifications'
import {
  emptyEmployerPostingPreferences,
  type EmployerPostingPreferences,
} from '@/lib/employer-posting-preferences'
import {
  loadEmployerPostingPreferences,
  saveEmployerPostingPreferences,
} from '@/lib/employer-posting-preferences-api'
import { authorizedFetch } from '@/lib/api'
import type { NotificationPreferenceItem } from '@/hooks/use-notifications'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'

interface EmployerPostingPreferencesStepProps {
  step: OnboardingStep
  orgSlug?: string
  allowSkipPostingPreferences?: boolean
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

async function loadNotificationPreferences(): Promise<NotificationPreferenceItem[]> {
  const response = await authorizedFetch('/api/notifications/preferences')
  if (!response.ok) {
    throw new Error('Failed to load notification settings')
  }
  return response.json()
}

export function EmployerPostingPreferencesStep({
  orgSlug,
  allowSkipPostingPreferences = false,
  onNext,
  onBack,
  isCompleting,
  canGoBack,
}: EmployerPostingPreferencesStepProps) {
  const defaultCountry = defaultEntityCountryCode(CLIENT_LOCALE)
  const [prefs, setPrefs] = useState<EmployerPostingPreferences>(() =>
    emptyEmployerPostingPreferences(defaultCountry),
  )
  const [notifyEmailEnabled, setNotifyEmailEnabled] = useState(true)
  const [notifyInAppEnabled, setNotifyInAppEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const [postingResult, notificationPrefs] = await Promise.all([
          loadEmployerPostingPreferences({
            orgSlug,
            defaultCountryCode: defaultCountry,
          }),
          loadNotificationPreferences().catch(() => [] as NotificationPreferenceItem[]),
        ])
        if (!cancelled) {
          setPrefs(postingResult.preferences)
          setNotifyEmailEnabled(applicationReceivedEmailEnabled(notificationPrefs))
          setNotifyInAppEnabled(applicationReceivedInAppEnabled(notificationPrefs))
        }
      } catch (err) {
        console.error('Failed to load posting preferences:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [defaultCountry, orgSlug])

  const handleContinue = async () => {
    setSaving(true)
    setError(null)

    try {
      await saveEmployerPostingPreferences({ preferences: prefs, orgSlug })
      await saveApplicationReceivedEmailPreference({
        emailEnabled: notifyEmailEnabled,
        inAppEnabled: notifyInAppEnabled,
      })
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      console.error('Error saving posting preferences:', err)
      setError(err instanceof Error ? err.message : 'Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading your preferences…</p>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        These defaults pre-fill new job postings. You can update them anytime in{' '}
        <span className="font-medium text-foreground">Organisation settings → Preferences</span>.
      </p>

      <EmployerPostingPreferencesFields
        layout="onboarding"
        prefs={prefs}
        onChange={setPrefs}
        notifyEmailEnabled={notifyEmailEnabled}
        onNotifyEmailChange={setNotifyEmailEnabled}
        disabled={saving || isCompleting}
      />

      {error ? <div className="text-sm text-red-600 dark:text-red-400">{error}</div> : null}

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleContinue}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting}
        isBusy={saving || isCompleting}
        continueLabel="Finish"
        continueBusyLabel="Finishing…"
        trailing={
          allowSkipPostingPreferences ? (
            <Button
              variant="ghost"
              onClick={() => void runOnboardingStepNext(onNext, setError)}
              disabled={saving || isCompleting}
            >
              Skip for now
            </Button>
          ) : null
        }
      />
    </div>
  )
}
